import { keyframes, styled, Typography } from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import WmCompass from '../assets/wm_compass_new.png';
import {
  getConversations,
  getFolderAgnosticConversations,
  getSharedConversation,
} from '../redux/actions/conversationActions';
import { getGlobalSearchResults } from '../redux/actions/globalSearchActions';
import { myFolder } from '../redux/consts';
import { setShowConversationsOnly } from '../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../redux/store';
import { StyledChatContainer } from '../theme/CustomComponents';
import { Conversation } from '../Types/conversation';

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ChatIcon = styled('img')({
  width: '20px',
});

const SpinningChatIcon = styled(ChatIcon)({
  animation: `${rotateAnimation} 2s linear infinite`,
});

const SharedConversation = (): JSX.Element => {
  const { conversationId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    if (conversationId) {
      dispatch(getSharedConversation(conversationId)).then(
        (action: PayloadAction<Conversation | unknown>) => {
          const isConversation = (payload: Conversation | unknown): payload is Conversation => {
            return (payload as Conversation).id !== undefined;
          };

          if (!isConversation(action.payload)) {
            enqueueSnackbar('There was an error accessing the conversation', {
              variant: 'error',
            });
            return navigate(`/folder/${myFolder.id}`);
          }

          const newConversation = action.payload;
          dispatch(setShowConversationsOnly(true));
          dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
          dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
          dispatch(getConversations()).then(() => {
            return navigate(`/folder/${myFolder.id}/${newConversation.id}`);
          });
        }
      );
    }
  }, [conversationId, dispatch, navigate]);

  return (
    <div style={{ overflow: 'auto', width: '100%', minHeight: 'calc(100% - 64px)' }}>
      <StyledChatContainer maxWidth="md">
        <div
          style={{
            marginTop: '80px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <SpinningChatIcon
            src={WmCompass}
            alt="West Monroe Compass"
            sx={{ width: '40px', height: '40px' }}
          />
          <Typography variant="h4">Copying Shared Conversation...</Typography>
        </div>
      </StyledChatContainer>
    </div>
  );
};

export default SharedConversation;
